import React, { useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import featuredLogistics from '../assets/why-oman/featured-logistics.png';
import satMining from '../assets/why-oman/satellite-mining.png';
import satIct from '../assets/why-oman/satellite-ict.png';
import satFisheries from '../assets/why-oman/satellite-fisheries.png';
import satRenewable from '../assets/why-oman/satellite-renewable.png';
import satTourism from '../assets/why-oman/satellite-tourism.png';
import satManufacturing from '../assets/why-oman/satellite-manufacturing.png';

const EXPLORE_SECTOR_URL = 'https://investoman.om/';

const pickLocalImage = (sectorName) => {
  const l = (sectorName || '').toLowerCase();
  if (l.includes('mining') || l.includes('تعدين')) return satMining;
  if (l.includes('ict') || l.includes('tech') || l.includes('تقنية') || l.includes('تكنولوجيا')) return satIct;
  if (l.includes('fish') || l.includes('مصايد') || l.includes('agri-food')) return satFisheries;
  if (l.includes('renew') || l.includes('طاقة متجددة') || l.includes('هيدروجين')) return satRenewable;
  if (l.includes('tour') || l.includes('سياح')) return satTourism;
  if (l.includes('manufact') || l.includes('تصنيع')) return satManufacturing;
  if (l.includes('logistics') || l.includes('لوجست')) return featuredLogistics;
  return featuredLogistics;
};

const fadeVariants = {
  initial: { opacity: 0.65, scale: 0.985 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0.65, scale: 0.985 },
};

/** Side peek cards — title at bottom, no CTA */
const SectorPeekCard = ({ sector, side, onActivate }) => {
  const { t } = useLanguage();
  if (!sector) return <div className="w-[26%] max-w-[150px] shrink-[1] aspect-[4/5] sm:aspect-[5/6] opacity-0" aria-hidden />;

  const isLeft = side === 'left';
  const transform = isLeft
    ? 'perspective(1100px) rotateY(10deg) scale(0.9)'
    : 'perspective(1100px) rotateY(-10deg) scale(0.9)';
  const origin = isLeft ? 'center right' : 'center left';

  return (
    <motion.button
      type="button"
      onClick={onActivate}
      className="relative w-[26%] min-w-[100px] max-w-[160px] shrink overflow-hidden rounded-[22px] border border-white/25 bg-black/20 shadow-lg ring-1 ring-black/10 sm:min-w-[120px] sm:max-w-[180px] sm:rounded-[26px]"
      style={{ transform, transformOrigin: origin }}
      aria-label={isLeft ? t('website.whyOman.sliderPrev') : t('website.whyOman.sliderNext')}
      whileTap={{ scale: 0.94 }}
      transition={{ type: 'spring', stiffness: 400, damping: 28 }}
    >
      <div className="aspect-[4/5] w-full sm:aspect-[5/6]">
        <img
          src={pickLocalImage(sector.name)}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/10" aria-hidden />
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-center px-2 pb-3 pt-8 sm:pb-4">
          <span
            className="w-full truncate text-center text-[12px] font-bold leading-tight text-white sm:text-[13px]"
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              textShadow: '0 2px 12px rgba(0,0,0,0.65)',
            }}
          >
            {sector.name}
          </span>
        </div>
      </div>
    </motion.button>
  );
};

/** Center hero card — title + glass Explore CTA */
const SectorHeroCard = ({ sector }) => {
  const { t } = useLanguage();
  if (!sector) return null;

  return (
    <div className="relative z-10 w-[min(78vw,400px)] shrink-0 overflow-hidden rounded-[24px] border border-white/30 bg-black/10 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.35)] ring-1 ring-white/15 sm:w-[min(72vw,420px)] sm:rounded-[28px] lg:w-[min(64vw,440px)]">
      <div className="relative aspect-[4/5] w-full sm:aspect-[16/9]">
        <img
          src={pickLocalImage(sector.name)}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-black/15" aria-hidden />
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-5 pb-7 pt-9 text-center sm:gap-5 sm:px-8 sm:pb-9 sm:pt-12">
          <h3
            className="max-w-md text-[22px] font-bold leading-tight text-white sm:text-[26px] lg:text-[30px]"
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              textShadow: '0 2px 28px rgba(0,0,0,0.5)',
            }}
          >
            {sector.name}
          </h3>
          <a
            href={EXPLORE_SECTOR_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-white/50 bg-black/45 px-6 py-2.5 text-[13px] font-semibold text-white shadow-[0_8px_32px_rgba(0,0,0,0.3)] backdrop-blur-[12px] transition hover:border-white/70 hover:bg-black/55 sm:px-7 sm:py-3 sm:text-[14px]"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            onClick={(e) => e.stopPropagation()}
          >
            {t('website.whyOman.exploreSector')}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden className="opacity-95">
              <path
                d="M5 12h14M13 6l6 6-6 6"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </a>
        </div>
      </div>
    </div>
  );
};

const WhyOmanSectorDeck = ({ sectors, activeIndex, setActiveIndex }) => {
  const { t, isRTL } = useLanguage();
  const n = sectors.length;
  const touchStartX = useRef(null);

  const goPrev = useCallback(() => {
    if (n <= 0) return;
    setActiveIndex((i) => (i - 1 + n) % n);
  }, [n, setActiveIndex]);

  const goNext = useCallback(() => {
    if (n <= 0) return;
    setActiveIndex((i) => (i + 1) % n);
  }, [n, setActiveIndex]);

  const goToIndex = useCallback(
    (i) => {
      if (i === activeIndex || n <= 0) return;
      setActiveIndex(i);
    },
    [activeIndex, n, setActiveIndex]
  );

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (dx > 56) goPrev();
    else if (dx < -56) goNext();
  };

  if (!n) return null;

  const prevIndex = (activeIndex - 1 + n) % n;
  const nextIndex = (activeIndex + 1) % n;
  const leftSector = isRTL ? sectors[nextIndex] : sectors[prevIndex];
  const rightSector = isRTL ? sectors[prevIndex] : sectors[nextIndex];
  const leftAction = isRTL ? goNext : goPrev;
  const rightAction = isRTL ? goPrev : goNext;
  const current = sectors[activeIndex % n];

  if (n === 1) {
    return (
      <div className="flex w-full flex-col items-center gap-8 sm:gap-10">
        <div className="w-full rounded-[28px] bg-[#E8EAEE] p-4 sm:p-6 lg:rounded-[32px] lg:p-8">
          <div className="mx-auto flex max-w-[480px] justify-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                variants={fadeVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.28, ease: [0.22, 0.61, 0.36, 1] }}
                className="w-full"
              >
                <SectorHeroCard sector={current} />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
        <div className="flex items-center justify-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-[#4C078C] shadow-sm ring-2 ring-[#4C078C]/25" aria-current="true" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-center gap-5 sm:gap-7 lg:gap-8">
      <div className="w-full rounded-[28px] bg-[#E8EAEE] p-2.5 sm:p-4 lg:rounded-[32px] lg:p-6">
        <div
          className="relative mx-auto max-w-[960px]"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          style={{ perspective: '1200px' }}
        >
          <button
            type="button"
            onClick={goPrev}
            className="absolute left-0 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200/90 bg-white/95 text-gray-600 shadow-md backdrop-blur-sm transition hover:border-[#4C078C]/30 hover:text-[#4C078C] sm:h-10 sm:w-10 md:left-1 lg:left-2"
            aria-label={t('website.whyOman.sliderPrev')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            type="button"
            onClick={goNext}
            className="absolute right-0 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200/90 bg-white/95 text-gray-600 shadow-md backdrop-blur-sm transition hover:border-[#4C078C]/30 hover:text-[#4C078C] sm:h-10 sm:w-10 md:right-1 lg:right-2"
            aria-label={t('website.whyOman.sliderNext')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <div className="flex items-center justify-center gap-1.5 overflow-visible px-10 sm:gap-3 sm:px-12 md:gap-5 md:px-14">
            {prevIndex !== nextIndex ? (
              <>
                <SectorPeekCard sector={leftSector} side="left" onActivate={leftAction} />

                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeIndex}
                    variants={fadeVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={{ duration: 0.3, ease: [0.22, 0.61, 0.36, 1] }}
                    className="flex shrink-0 justify-center"
                  >
                    <SectorHeroCard sector={current} />
                  </motion.div>
                </AnimatePresence>

                <SectorPeekCard sector={rightSector} side="right" onActivate={rightAction} />
              </>
            ) : (
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIndex}
                  variants={fadeVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={{ duration: 0.3, ease: [0.22, 0.61, 0.36, 1] }}
                  className="flex w-full max-w-[480px] justify-center"
                >
                  <SectorHeroCard sector={current} />
                </motion.div>
              </AnimatePresence>
            )}
          </div>
        </div>
      </div>

      <div className="flex w-full max-w-lg items-center justify-center gap-2 px-4 sm:max-w-none sm:gap-2.5">
        {sectors.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => goToIndex(i)}
            className={`rounded-full transition-all duration-300 ${
              i === activeIndex
                ? 'h-2.5 w-2.5 scale-110 bg-[#4C078C] shadow-sm ring-2 ring-[#4C078C]/25'
                : 'h-2 w-2 bg-[#D1D5DB] hover:bg-[#B8BCC4]'
            }`}
            aria-label={t('website.whyOman.sliderDot', { n: i + 1 })}
            aria-current={i === activeIndex}
          />
        ))}
      </div>
    </div>
  );
};

export default WhyOmanSectorDeck;
